import { useCallback, useEffect, useState } from "react";
import { supabase } from "../admin/supabase";
import { getContentCollection } from "../services/contentService";

/**
 * Reads an editable content collection from the backend and keeps it in sync
 * live (admin saves appear on the public site without a refresh).
 *
 * `fallback` is the original in-code content. It is used whenever the
 * collection has no published rows or the request fails, so the page always
 * renders something meaningful.
 */
export default function useContent(collection, fallback = []) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (active = { current: true }) => {
      try {
        const data = await getContentCollection(collection);
        if (active.current) {
          setItems(data.length ? data : null);
          setError(null);
        }
      } catch (loadError) {
        if (active.current) setError(loadError);
      } finally {
        if (active.current) setLoading(false);
      }
    },
    [collection]
  );

  useEffect(() => {
    const active = { current: true };
    load(active);

    const channel = supabase
      .channel(`public-content-${collection}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "content_items", filter: `collection=eq.${collection}` },
        () => load(active)
      )
      .subscribe();

    return () => {
      active.current = false;
      supabase.removeChannel(channel);
    };
  }, [collection, load]);

  return {
    items: items || fallback,
    fromBackend: Boolean(items),
    loading,
    error,
  };
}
